import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Upload, ClipboardList, BarChart3, ChevronRight,
  Users, Award, PlayCircle, ArrowRight, Sparkles,
} from 'lucide-react';
import { BACKEND_URL } from '../lib/config';

function StatCard({ icon: Icon, label, value, color = 'text-primary' }) {
  return (
    <Card className="bg-[#111827] border-border/60 card-hover">
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs text-muted-foreground mb-1">{label}</p>
            <p className={`text-3xl font-bold ${color}`}>{value ?? '–'}</p>
          </div>
          <div className="w-10 h-10 bg-secondary/70 rounded-xl flex items-center justify-center">
            <Icon size={18} className={color} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function CoachDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/coach/stats`)
      .then(r => setStats(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  return (
    <Layout>
      <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-6">

        {/* ── Hero CTA ─────────────────────────────────────────────────────────── */}
        <div
          className="relative overflow-hidden rounded-2xl border border-[#2BB5D8]/25"
          style={{ background: 'linear-gradient(135deg, #0f2544 0%, #111827 60%)' }}
        >
          {/* Subtle decorative circle */}
          <div
            className="absolute -top-16 -right-16 w-64 h-64 rounded-full opacity-5"
            style={{ background: '#2BB5D8' }}
          />
          <div className="relative z-10 p-6 sm:p-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Sparkles size={16} className="text-[#2BB5D8]" />
                  <span className="text-xs font-semibold text-[#2BB5D8] uppercase tracking-wider">
                    Primary Action
                  </span>
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold leading-tight">
                  Start a New Evaluation
                </h2>
                <p className="text-sm text-muted-foreground max-w-md">
                  Upload your Excel sheet, review player scores, enhance remarks with AI,
                  preview evaluation cards, and generate professional PDFs — all in one flow.
                </p>
              </div>
              <Button
                data-testid="start-evaluation-hero-btn"
                size="lg"
                className="shrink-0 bg-[#2BB5D8] hover:bg-[#25a3c4] text-[#0D1117] font-bold text-base px-7 py-4 h-auto rounded-xl shadow-lg shadow-[#2BB5D8]/20 transition-transform hover:scale-[1.02]"
                onClick={() => navigate('/coach/new-evaluation')}
              >
                <PlayCircle size={20} className="mr-2" />
                Start Evaluation
                <ArrowRight size={16} className="ml-2" />
              </Button>
            </div>

            {/* Mini step guide */}
            <div className="mt-5 pt-5 border-t border-white/5 grid grid-cols-3 sm:grid-cols-6 gap-2">
              {[
                'Select Batch',
                'Upload Excel',
                'Review Players',
                'AI Remarks',
                'Preview Card',
                'Generate PDFs',
              ].map((step, i) => (
                <div key={step} className="flex flex-col items-center gap-1 text-center">
                  <div className="w-6 h-6 rounded-full bg-[#2BB5D8]/20 border border-[#2BB5D8]/30 flex items-center justify-center text-[10px] font-bold text-[#2BB5D8]">
                    {i + 1}
                  </div>
                  <span className="text-[10px] text-muted-foreground leading-tight hidden sm:block">{step}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Welcome & header stats row ───────────────────────────────────────── */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">My Dashboard</h1>
            <p className="text-sm text-muted-foreground">Welcome back, {user?.name}</p>
          </div>
          <Button
            data-testid="new-evaluation-btn"
            variant="outline"
            className="border-[#2BB5D8]/40 text-[#2BB5D8] hover:bg-[#2BB5D8]/10"
            onClick={() => navigate('/coach/new-evaluation')}
          >
            <Upload size={15} className="mr-1.5" /> New Evaluation
          </Button>
        </div>

        {/* ── Stats ────────────────────────────────────────────────────────────── */}
        <div className="grid grid-cols-3 gap-4">
          <StatCard
            icon={ClipboardList}
            label="My Evaluations"
            value={loading ? '…' : stats?.total_evaluations}
          />
          <StatCard
            icon={Users}
            label="Total Players"
            value={loading ? '…' : stats?.total_players}
            color="text-[#F7941D]"
          />
          <StatCard
            icon={Award}
            label="PDFs Generated"
            value={loading ? '…' : stats?.pdfs_generated}
            color="text-green-400"
          />
        </div>

        {/* ── Quick actions ─────────────────────────────────────────────────────── */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {[
            {
              label: 'My Evaluations',
              icon: ClipboardList,
              path: '/coach/evaluations',
              color: 'from-orange-900/40 to-orange-900/10',
              desc: 'View & manage your uploads',
            },
            {
              label: 'Analytics',
              icon: BarChart3,
              path: '/coach/analytics',
              color: 'from-green-900/40 to-green-900/10',
              desc: 'Insights from your data',
            },
            {
              label: 'Download Reports',
              icon: Award,
              path: '/coach/evaluations',
              color: 'from-purple-900/40 to-purple-900/10',
              desc: 'PDFs and exports',
            },
          ].map(({ label, icon: Icon, path, color, desc }) => (
            <Link
              key={path + label}
              to={path}
              className={`p-4 rounded-xl bg-gradient-to-br ${color} border border-border/40 hover:border-primary/30 transition-colors`}
            >
              <Icon size={20} className="text-primary mb-2" />
              <p className="font-semibold text-sm">{label}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>
            </Link>
          ))}
        </div>

        {/* ── Recent Evaluations ────────────────────────────────────────────────── */}
        <Card className="bg-[#111827] border-border/60">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Recent Evaluations</CardTitle>
              <Link to="/coach/evaluations">
                <Button variant="ghost" size="sm" className="text-xs text-primary">
                  View all <ChevronRight size={14} />
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-2">
                {[1, 2, 3].map(i => (
                  <div key={i} className="h-10 bg-secondary/50 rounded animate-pulse" />
                ))}
              </div>
            ) : stats?.recent_evaluations?.length ? (
              <div className="space-y-2">
                {stats.recent_evaluations.map(ev => (
                  <Link
                    key={ev.id}
                    to={`/coach/evaluations/${ev.id}`}
                    className="flex items-center justify-between p-3 rounded-lg hover:bg-secondary/50 transition-colors"
                  >
                    <div>
                      <p className="text-sm font-medium">{ev.cycle_name}</p>
                      <p className="text-xs text-muted-foreground">
                        {ev.batch_name} · {ev.location_name}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">{ev.player_count} players</Badge>
                      <ChevronRight size={14} className="text-muted-foreground" />
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-10">
                <ClipboardList size={36} className="mx-auto text-muted-foreground mb-3" />
                <p className="text-sm font-medium mb-1">No evaluations yet</p>
                <p className="text-xs text-muted-foreground mb-4">
                  Click &quot;Start Evaluation&quot; above to upload your first batch.
                </p>
                <Button
                  className="bg-[#2BB5D8] hover:bg-[#25a3c4] text-[#0D1117] font-semibold"
                  onClick={() => navigate('/coach/new-evaluation')}
                >
                  <Upload size={14} className="mr-1.5" /> Start First Evaluation
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
